import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { addDays, format, startOfDay, subDays } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { ChartCardSkeleton, PageHeaderSkeleton } from "@/components/loading";
import {
  DashboardContentZone,
  DashboardControlGroup,
  DashboardControlRow,
} from "@/components/patterns/content-zone";
import { DashboardPageHeader, DashboardPageShell } from "@/components/patterns/headers";
import { brandControlLabelClassName } from "@/components/ui/brand-typography";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { brandInk, brandInkAlpha } from "@/lib/brand-tokens";
import { USAGE_CHART_COLOR, resolveCssColor } from "@/lib/chart-colors";
import { useTRPC } from "@/lib/trpc";

export const Route = createFileRoute("/_dashboard/usage")({
  component: RouteComponent,
});

type TimeRange = "week" | "month" | "3months";

interface ProcessedData {
  date: string;
  credits: number;
  usage: number;
  subscription: number;
  extra: number;
  expired: number;
  cumulative: number;
}

const TIME_RANGE_LABELS: Record<TimeRange, string> = {
  week: "Last 7 days",
  month: "Last 30 days",
  "3months": "Last 3 months",
};

function RouteComponent() {
  const trpc = useTRPC();
  const [timeRange, setTimeRange] = useState<TimeRange>("week");

  const calculateStartDate = useCallback(() => {
    switch (timeRange) {
      case "week":
        return subDays(new Date(), 7);
      case "month":
        return subDays(new Date(), 30);
      case "3months":
        return subDays(new Date(), 90);
    }
  }, [timeRange]);

  const startDate = useMemo(() => calculateStartDate(), [calculateStartDate]);
  const endDate = useMemo(() => addDays(new Date(), 1), []);

  const { data: billingRecords, isLoading } = useQuery(
    trpc.billingRecords.getBillingRecordsByDate.queryOptions({ startDate, endDate }),
  );

  const filteredRecords = useMemo(() => {
    if (!billingRecords?.items.length) return [];
    const now = new Date();
    let start: Date;
    switch (timeRange) {
      case "week":
        start = subDays(now, 7);
        break;
      case "3months":
        start = subDays(now, 90);
        break;
      default:
        start = subDays(now, 30);
        break;
    }
    return billingRecords.items.filter((record: { createdAt: string | Date }) => {
      return new Date(record.createdAt) >= start;
    });
  }, [billingRecords, timeRange]);

  const chartData = useMemo(() => {
    const dailyData = new Map<string, ProcessedData>();
    const now = new Date();
    const days = timeRange === "week" ? 7 : timeRange === "3months" ? 90 : 30;

    for (let i = days - 1; i >= 0; i--) {
      const date = subDays(now, i);
      const dateKey = format(startOfDay(date), "yyyy-MM-dd");
      dailyData.set(dateKey, {
        date: dateKey,
        credits: 0,
        usage: 0,
        subscription: 0,
        extra: 0,
        expired: 0,
        cumulative: 0,
      });
    }

    filteredRecords.forEach((record) => {
      const dateKey = format(startOfDay(new Date(record.createdAt)), "yyyy-MM-dd");
      if (dailyData.has(dateKey)) {
        const dayData = dailyData.get(dateKey);
        if (!dayData) return;
        let transformedCredits = record.credits;
        if (record.type === "USAGE") {
          transformedCredits = -Math.abs(record.credits);
          dayData.usage += transformedCredits;
        } else if (record.type === "EXPIRED") {
          transformedCredits = -Math.abs(record.credits);
          dayData.expired += transformedCredits;
        } else {
          transformedCredits = Math.abs(record.credits);
          if (record.type === "SUBSCRIPTION") {
            dayData.subscription += transformedCredits;
          } else if (record.type === "EXTRA") {
            dayData.extra += transformedCredits;
          }
        }
        dayData.credits += transformedCredits;
      }
    });

    const sortedData = Array.from(dailyData.values()).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );

    let runningTotal = 0;
    return sortedData.map((day) => {
      runningTotal += day.credits;
      return {
        ...day,
        cumulative: runningTotal,
        displayDate: format(new Date(day.date), "MMM d"),
      };
    });
  }, [filteredRecords, timeRange]);

  const resolvedUsageChartColor = useMemo(() => resolveCssColor(USAGE_CHART_COLOR), []);
  const chartConfig = useMemo(
    () => ({
      credits: { label: "Net Credits", color: resolveCssColor("var(--chart-1)") },
      usage: { label: "Usage", color: resolveCssColor("var(--destructive)") },
      subscription: { label: "Subscription", color: resolveCssColor("var(--chart-3)") },
      extra: { label: "Extra Credits", color: resolveCssColor("var(--chart-4)") },
      expired: { label: "Expired Credits", color: resolveCssColor("var(--chart-5)") },
    }),
    [],
  );

  const totalUsage = chartData.reduce((sum, day) => sum + day.usage, 0);
  const totalSubscription = chartData.reduce((sum, day) => sum + day.subscription, 0);
  const totalExtra = chartData.reduce((sum, day) => sum + day.extra, 0);
  const netChange = chartData.reduce((sum, day) => sum + day.credits, 0);

  if (isLoading) {
    return (
      <DashboardPageShell as="div" contentClassName="space-y-6">
        <PageHeaderSkeleton compact />
        <DashboardContentZone>
          <DashboardControlRow>
            <div />
            <DashboardControlGroup className="md:justify-end">
              <span className={brandControlLabelClassName}>Display Period</span>
              <Skeleton className="h-10 w-full rounded-lg sm:w-[140px]" />
            </DashboardControlGroup>
          </DashboardControlRow>
          <ChartCardSkeleton heightClassName="h-[400px]" legendItems={5} />
        </DashboardContentZone>
      </DashboardPageShell>
    );
  }

  return (
    <DashboardPageShell as="div" contentClassName="space-y-6">
      <DashboardPageHeader
        title="Usage"
        description={
          <>
            {TIME_RANGE_LABELS[timeRange]}
            {totalUsage < 0 && (
              <>
                {" "}
                &middot;{" "}
                <span className="font-semibold text-destructive">
                  {Math.abs(totalUsage).toLocaleString()} credits consumed
                </span>
              </>
            )}
          </>
        }
      />

      <DashboardContentZone>
        <DashboardControlRow>
          <div />
          <DashboardControlGroup className="md:justify-end">
            <span className={brandControlLabelClassName}>Display Period</span>
            <Select
              value={timeRange}
              onValueChange={(value: TimeRange) => setTimeRange(value)}
            >
              <SelectTrigger
                variant="brand"
                size="sm"
                className="w-full cursor-pointer sm:w-[140px]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week" className="text-xs">Last 7 days</SelectItem>
                <SelectItem value="month" className="text-xs">Last 30 days</SelectItem>
                <SelectItem value="3months" className="text-xs">Last 3 months</SelectItem>
              </SelectContent>
            </Select>
          </DashboardControlGroup>
        </DashboardControlRow>

        {/* Main panel */}
        <div className="min-w-0 overflow-hidden rounded-xl border border-brand-ink/12 shadow-sm">
          <div className="border-b border-brand-ink/[0.08] bg-brand-ink/[0.05] px-5 py-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-brand-ink/40">
              Credit Activity
            </span>
          </div>

          {/* Chart */}
          <div className="overflow-hidden bg-white px-2 pt-5 pb-2">
            <ChartContainer config={chartConfig} className="h-[340px] w-full">
              <ResponsiveContainer width="99%" height="100%">
                <AreaChart
                  data={chartData}
                  margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
                >
                  <defs>
                    <linearGradient id="usageGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={resolvedUsageChartColor} stopOpacity={0.18} />
                      <stop offset="100%" stopColor={resolvedUsageChartColor} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="usageGradientLine" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={resolvedUsageChartColor} stopOpacity={1} />
                      <stop offset="100%" stopColor={resolvedUsageChartColor} stopOpacity={0.6} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid
                    strokeDasharray="4 4"
                    stroke={brandInkAlpha(0.08)}
                    vertical={false}
                  />

                  <XAxis
                    dataKey="displayDate"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: brandInkAlpha(0.45), fontWeight: 500 }}
                    dy={8}
                    interval="preserveStartEnd"
                  />

                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: brandInkAlpha(0.45), fontWeight: 500 }}
                    dx={-4}
                    width={48}
                  />

                  <ChartTooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const data = payload[0].payload;
                      return (
                        <div
                          className="min-w-[180px] rounded-lg p-3 text-xs shadow-lg"
                          style={{
                            background: "white",
                            border: `1px solid ${brandInkAlpha(0.12)}`,
                            color: brandInk,
                          }}
                        >
                          <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-brand-ink/50">
                            {label}
                          </p>
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between gap-6">
                              <span className="text-brand-ink/60">Balance</span>
                              <span
                                className="font-semibold tabular-nums"
                                style={{
                                  color:
                                    data.cumulative >= 0
                                      ? "var(--color-chart-3)"
                                      : "var(--destructive)",
                                }}
                              >
                                {data.cumulative >= 0 ? "+" : ""}
                                {data.cumulative.toLocaleString()}
                              </span>
                            </div>
                            {data.credits !== 0 && (
                              <div className="flex items-center justify-between gap-6">
                                <span className="text-brand-ink/60">Daily</span>
                                <span
                                  className="font-semibold tabular-nums"
                                  style={{
                                    color:
                                      data.credits >= 0
                                        ? "var(--color-chart-3)"
                                        : "var(--destructive)",
                                  }}
                                >
                                  {data.credits >= 0 ? "+" : ""}
                                  {data.credits.toLocaleString()}
                                </span>
                              </div>
                            )}
                            {data.usage < 0 && (
                              <div className="flex items-center justify-between gap-6">
                                <span className="text-brand-ink/60">Usage</span>
                                <span
                                  className="font-semibold tabular-nums"
                                  style={{ color: "var(--destructive)" }}
                                >
                                  {data.usage.toLocaleString()}
                                </span>
                              </div>
                            )}
                            {data.subscription > 0 && (
                              <div className="flex items-center justify-between gap-6">
                                <span className="text-brand-ink/60">Subscription</span>
                                <span
                                  className="font-semibold tabular-nums"
                                  style={{ color: "var(--color-chart-3)" }}
                                >
                                  +{data.subscription.toLocaleString()}
                                </span>
                              </div>
                            )}
                            {data.extra > 0 && (
                              <div className="flex items-center justify-between gap-6">
                                <span className="text-brand-ink/60">Extra</span>
                                <span
                                  className="font-semibold tabular-nums"
                                  style={{ color: "var(--info)" }}
                                >
                                  +{data.extra.toLocaleString()}
                                </span>
                              </div>
                            )}
                            {data.expired < 0 && (
                              <div className="flex items-center justify-between gap-6">
                                <span className="text-brand-ink/60">Expired</span>
                                <span
                                  className="font-semibold tabular-nums"
                                  style={{ color: "var(--warning)" }}
                                >
                                  {data.expired.toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }}
                  />

                  <Area
                    type="monotone"
                    dataKey="cumulative"
                    stroke={resolvedUsageChartColor}
                    strokeWidth={2}
                    fill="url(#usageGradient)"
                    dot={false}
                    activeDot={{
                      r: 4,
                      stroke: resolvedUsageChartColor,
                      strokeWidth: 2,
                      fill: "white",
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartContainer>
          </div>

          {/* Summary strip */}
          <div className="grid grid-cols-1 gap-px border-t border-brand-ink/[0.08] bg-brand-ink/[0.08] sm:grid-cols-2 xl:grid-cols-4">
            <div className="bg-white px-5 py-4 flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-brand-ink/40">
                Usage
              </span>
              <span
                className="text-sm font-semibold tabular-nums"
                style={{ color: totalUsage < 0 ? "var(--destructive)" : brandInkAlpha(0.5) }}
              >
                {totalUsage.toLocaleString()}
              </span>
            </div>

            <div className="bg-white px-5 py-4 flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-brand-ink/40">
                Subscription
              </span>
              <span
                className="text-sm font-semibold tabular-nums"
                style={{
                  color: totalSubscription > 0 ? "var(--success)" : brandInkAlpha(0.5),
                }}
              >
                {totalSubscription > 0 ? "+" : ""}
                {totalSubscription.toLocaleString()}
              </span>
            </div>

            <div className="bg-white px-5 py-4 flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-brand-ink/40">
                Extra credits
              </span>
              <span
                className="text-sm font-semibold tabular-nums"
                style={{ color: totalExtra > 0 ? "var(--info)" : brandInkAlpha(0.5) }}
              >
                {totalExtra > 0 ? "+" : ""}
                {totalExtra.toLocaleString()}
              </span>
            </div>

            <div className="bg-white px-5 py-4 flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-brand-ink/40">
                Net change
              </span>
              <span
                className="text-sm font-semibold tabular-nums"
                style={{
                  color:
                    netChange > 0
                      ? "var(--success)"
                      : netChange < 0
                        ? "var(--destructive)"
                        : brandInkAlpha(0.5),
                }}
              >
                {netChange > 0 ? "+" : ""}
                {netChange.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </DashboardContentZone>
    </DashboardPageShell>
  );
}
